import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  accountVoicesKey,
  bookKey,
  castAccountVoice,
  castKey,
  castLibraryVoice,
  chooseVoice,
  findLibraryVoices,
  getAccountVoices,
  designVoices,
  getCast,
  sampleVoice,
  updateBook,
  type CastRole,
  type LibraryVoice,
} from "../api";
import { Amount } from "../components/Amount";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { formatMicros } from "./money";
import { useGrowToText } from "./useGrowToText";

/** Playable is anything the panel's one player plays: a sample, or a library voice's free preview. */
type Playable = { id: string; sample_url: string };

/**
 * VoicesPanel casts a book's voices (ADR-0157, requirement 0090): the narrator
 * and every character with lines, each designed from a description the author
 * can edit, heard as two candidates reading that role's own words from the
 * book, and chosen by ear.
 *
 * A design spends, so its price is on the button. Choosing saves the voice on
 * the vendor at once — a candidate expires there — and casting another later
 * replaces it.
 *
 * Taking a voice the account holds, or one from the library, costs nothing
 * (ADR-0172): the card then plays the maker's preview when there is one, and
 * hearing the voice read this role's lines is a press of its own, with its
 * price on it.
 *
 * The extra voices (ADR-0157 §9) follow under their own heading: a card per
 * kind, cast the same way, read for the lines the roster leaves to someone
 * else. They never become the book's voice.
 *
 * Only roles that speak are listed (the owner, 2026-10-09): the cast endpoint
 * leaves out a form or an extra kind with no line unless it has a voice
 * already, so the panel shows what it is given.
 *
 * Phone-first: one card per role, stacked; play and choose are large targets;
 * no native select (the owner's rule).
 */
export default function VoicesPanel({ bookID }: { bookID: string }) {
  const t = useT();
  const cast = useQuery({ queryKey: castKey(bookID), queryFn: () => getCast(bookID) });
  const [playing, setPlaying] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  // One element for every sample, as the voice picker does.
  useEffect(() => {
    const el = new Audio();
    el.addEventListener("ended", () => setPlaying(null));
    audio.current = el;
    return () => {
      el.pause();
      audio.current = null;
    };
  }, []);

  function play(v: Playable) {
    const el = audio.current;
    if (!el) return;
    if (playing === v.id) {
      el.pause();
      setPlaying(null);
      return;
    }
    el.pause();
    el.src = v.sample_url;
    setPlaying(v.id);
    void el.play().catch(() => setPlaying(null));
  }

  if (cast.isPending) return <div aria-busy="true" />;
  if (cast.isError) {
    return (
      <p role="alert" className="text-sm text-red-600 dark:text-red-400">
        {t.voices.loadFailed}
      </p>
    );
  }
  const c = cast.data;
  if (!c.offered) {
    return (
      <p data-testid="voices-not-offered" className="text-sm text-muted">
        {c.reason || t.voices.notOffered}
      </p>
    );
  }
  const main = c.roles.filter((r) => !r.extra);
  const extras = c.roles.filter((r) => r.extra);
  const sections = (roles: CastRole[], group: string) => (
    <RoleSections
      roles={roles}
      group={group}
      render={(role) => (
        // Keyed by the accent too: a card keeps the description it opened
        // with, and a new accent re-writes the pre-filled ones.
        <RoleCard
          key={`${roleID(role)}-${c.accent ?? ""}`}
          bookID={bookID}
          role={role}
          playing={playing}
          onPlay={play}
        />
      )}
    />
  );
  return (
    <div data-testid="voices-panel">
      <p className="mb-6 text-xs text-muted">{t.voices.note}</p>
      {c.accent && c.accents && (
        <BookAccent bookID={bookID} accent={c.accent} accents={c.accents} />
      )}
      {sections(main, "main")}
      {extras.length > 0 && (
        <section className="mt-10" data-testid="voices-extras">
          <h2 className="text-lg font-medium">{t.voices.extrasTitle}</h2>
          <p className="mb-6 mt-1 text-xs text-muted">{t.voices.extrasNote}</p>
          {sections(extras, "extras")}
        </section>
      )}
    </div>
  );
}

/**
 * BookAccent is the accent the book's voices speak in (ADR-0164): pill chips,
 * never a select, drawn from the set the cast names. Choosing one writes the
 * book and re-reads the cast, whose pre-filled descriptions and library
 * searches then follow it. It re-casts nothing and costs nothing.
 */
function BookAccent({
  bookID,
  accent,
  accents,
}: {
  bookID: string;
  accent: string;
  accents: string[];
}) {
  const t = useT();
  const client = useQueryClient();
  const choose = useMutation({
    mutationFn: (voice_accent: string) => updateBook(bookID, { voice_accent }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: castKey(bookID) });
      void client.invalidateQueries({ queryKey: ["library-voices", bookID] });
    },
  });
  return (
    <div className="mb-8" data-testid="voices-accent">
      <p className="text-sm font-medium">{t.voices.accent}</p>
      <p className="mt-1 mb-2 text-xs text-muted">{t.voices.accentNote}</p>
      <div className="flex flex-wrap gap-2">
        {accents.map((a) => (
          <button
            key={a}
            type="button"
            data-testid={`voices-accent-${a}`}
            aria-pressed={a === accent}
            disabled={choose.isPending}
            onClick={() => {
              if (a !== accent) choose.mutate(a);
            }}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${
              a === accent ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
            }`}
          >
            {t.voices.accentName(a)}
          </button>
        ))}
      </div>
      {choose.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.voices.accentFailed}
        </p>
      )}
    </div>
  );
}

/**
 * RoleSections splits roles into those that need a voice and those that have
 * one (the owner, 2026-10-08), each under a heading with its count, keeping
 * the cast's order inside each: a role moves across when it is cast.
 */
function RoleSections({
  roles,
  group,
  render,
}: {
  roles: CastRole[];
  group: string;
  render: (role: CastRole) => React.ReactNode;
}) {
  const t = useT();
  const needs = roles.filter((r) => !r.voice);
  const has = roles.filter((r) => r.voice);
  return (
    <>
      {[
        { key: "needs", title: t.voices.needsVoice(needs.length), list: needs },
        { key: "has", title: t.voices.hasVoice(has.length), list: has },
      ]
        .filter((sec) => sec.list.length > 0)
        .map((sec) => (
          <section
            key={sec.key}
            className="mt-6 first:mt-0"
            data-testid={`voices-${group}-${sec.key}`}
          >
            <h3 className="mb-3 text-sm font-medium text-ink-soft">{sec.title}</h3>
            <ul className="space-y-6">{sec.list.map(render)}</ul>
          </section>
        ))}
    </>
  );
}

/** roleID names a role in test ids and keys: a character's or a form's id, an extra kind, or "narrator". */
function roleID(role: CastRole): string {
  return role.form_id ?? role.character_id ?? role.extra ?? "narrator";
}

/** isNarrator is the role whose voice is the book's voice. */
function isNarrator(role: CastRole): boolean {
  return !role.character_id && !role.extra && !role.form_id;
}

function RoleCard({
  bookID,
  role,
  playing,
  onPlay,
}: {
  bookID: string;
  role: CastRole;
  playing: string | null;
  onPlay: (v: Playable) => void;
}) {
  const t = useT();
  const client = useQueryClient();
  const [description, setDescription] = useState(role.description);
  const descriptionBox = useGrowToText(description);
  const id = roleID(role);

  const refresh = () => {
    void client.invalidateQueries({ queryKey: castKey(bookID) });
  };
  const design = useMutation({
    mutationFn: () => designVoices(bookID, role, description),
    onSuccess: refresh,
  });
  const choose = useMutation({
    mutationFn: (candidateID: string) => chooseVoice(bookID, candidateID),
    onSuccess: () => {
      refresh();
      // The narrator's voice is the book's voice.
      if (isNarrator(role))
        void client.invalidateQueries({ queryKey: bookKey(bookID, { draft: true }) });
    },
  });
  // The press of ADR-0172: this role's lines, once, on its cast voice.
  const sample = useMutation({
    mutationFn: () => sampleVoice(bookID, role),
    onSuccess: refresh,
  });
  const busy = design.isPending || choose.isPending || sample.isPending;
  // What the chosen voice's button plays: our sample, else the maker's preview.
  const clip = role.voice?.sample_url || role.voice?.preview_url || "";

  return (
    <li data-testid={`voice-role-${id}`} className="rounded-card border border-line p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-medium">
          <span dir="auto">
            {role.extra
              ? t.voices.extra[role.extra]
              : role.character_id || role.form_id
                ? role.name
                : t.voices.narrator}
          </span>
          {/* A form's voice (ADR-0159): whose shape it is, under its name. */}
          {role.form_id && (
            <span className="ms-2 text-xs font-normal text-muted" dir="auto">
              {role.role}
            </span>
          )}
        </h2>
        {!isNarrator(role) && (
          <span className="text-xs text-muted">{t.voices.lines(role.lines)}</span>
        )}
      </div>
      {role.voice && (
        <div className="mt-3 flex items-center gap-3" data-testid={`voice-chosen-${id}`}>
          {clip && (
            <PlayButton
              voice={{ id: role.voice.id, sample_url: clip }}
              playing={playing}
              onPlay={onPlay}
              label={t.voices.playChosen}
            />
          )}
          <span className="text-sm" dir="auto">
            {role.voice.source !== "designed" && role.voice.label
              ? t.voices.chosenNamed(role.voice.label)
              : t.voices.chosen}
          </span>
        </div>
      )}
      {role.voice && role.voice.source !== "designed" && clip && (
        <p className="mt-1 text-xs text-muted" data-testid={`voice-plays-${id}`}>
          {role.voice.sample_url ? t.voices.playsSample : t.voices.playsPreview}
        </p>
      )}
      {role.voice && !role.voice.sample_url && (
        <button
          type="button"
          data-testid={`voice-sample-${id}`}
          disabled={busy || !role.sample_text}
          onClick={() => sample.mutate()}
          className="mt-2 min-h-11 rounded-control border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {sample.isPending ? (
            t.voices.sampling
          ) : (
            <Amount sentence={t.voices.sample} amount={formatMicros(role.sample_micros)} />
          )}
        </button>
      )}
      {role.voice?.source === "library" && (
        <p className="mt-1 text-xs text-muted" data-testid={`voice-library-note-${id}`}>
          {t.voices.libraryChosen}
        </p>
      )}
      {!role.voice && (
        <p className="mt-2 text-xs text-muted" data-testid={`voice-none-${id}`}>
          {isNarrator(role) ? t.voices.narratorStock : t.voices.noVoice}
        </p>
      )}

      <label className="mt-4 block text-xs text-muted" htmlFor={`voice-description-${id}`}>
        {t.voices.description}
      </label>
      <textarea
        ref={descriptionBox}
        id={`voice-description-${id}`}
        data-testid={`voice-description-${id}`}
        dir="auto"
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="mt-1 block w-full resize-none overflow-hidden rounded-control border border-line bg-surface px-3 py-2 text-sm leading-relaxed"
      />
      {role.sample_text && (
        <p className="mt-2 text-xs text-muted">
          {t.voices.reads} <q dir="auto">{role.sample_text}</q>
        </p>
      )}
      <button
        type="button"
        data-testid={`voice-design-${id}`}
        disabled={busy || !role.sample_text || description.trim().length < 20}
        onClick={() => design.mutate()}
        className="mt-3 min-h-11 w-full rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
      >
        {design.isPending ? (
          t.voices.designing
        ) : (
          <Amount
            sentence={(a) =>
              role.candidates.length > 0 ? t.voices.designAgain(a) : t.voices.design(a)
            }
            amount={formatMicros(role.estimate_micros)}
          />
        )}
      </button>

      {role.candidates.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid={`voice-candidates-${id}`}>
          {role.candidates.map((v, i) => (
            <li key={v.id} className="flex items-center gap-3">
              <PlayButton
                voice={v}
                playing={playing}
                onPlay={onPlay}
                label={t.voices.playCandidate(i + 1)}
              />
              <span className="flex-1 text-sm">{t.voices.candidate(i + 1)}</span>
              <button
                type="button"
                data-testid={`voice-choose-${v.id}`}
                disabled={busy}
                onClick={() => choose.mutate(v.id)}
                className="min-h-11 rounded-control border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
              >
                {choose.isPending && choose.variables === v.id
                  ? t.voices.choosing
                  : t.voices.choose}
              </button>
            </li>
          ))}
        </ul>
      )}
      <MutationError
        className="mt-2"
        testId={`voice-error-${id}`}
        error={design.error ?? choose.error ?? sample.error}
        fallback={t.voices.failed}
      />
      <ExistingVoices bookID={bookID} role={role} disabled={busy} />
      <LibraryVoices
        bookID={bookID}
        role={role}
        description={description}
        disabled={busy}
        playing={playing}
        onPlay={onPlay}
      />
    </li>
  );
}

/**
 * A voice the vendor account already holds — made by hand outside the product
 * — taken for a role instead of a design. Listed as chips when the key may
 * list them; otherwise its id is pasted and taken unchecked: the first paid
 * use of it — its sample, or narration — is the check that the voice exists.
 * Taking one costs nothing (ADR-0172).
 */
function ExistingVoices({
  bookID,
  role,
  disabled,
}: {
  bookID: string;
  role: CastRole;
  disabled: boolean;
}) {
  const t = useT();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [voiceID, setVoiceID] = useState("");
  const [label, setLabel] = useState("");
  const id = roleID(role);
  const list = useQuery({
    queryKey: accountVoicesKey(bookID),
    queryFn: () => getAccountVoices(bookID),
    enabled: open,
  });
  const use = useMutation({
    mutationFn: (v: { voiceID: string; label: string }) =>
      castAccountVoice(bookID, role, v.voiceID, v.label),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: castKey(bookID) });
      if (isNarrator(role))
        void client.invalidateQueries({ queryKey: bookKey(bookID, { draft: true }) });
      setOpen(false);
    },
  });
  const busy = disabled || use.isPending;
  const current = role.voice?.vendor_voice_id;

  return (
    <div className="mt-4">
      <button
        type="button"
        data-testid={`voice-existing-${id}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="min-h-11 text-sm text-sage-deep underline-offset-4 hover:underline"
      >
        {open ? t.voices.hideExisting : t.voices.useExisting}
      </button>
      {open && (
        <div className="mt-2 space-y-3" data-testid={`voice-existing-panel-${id}`}>
          <p className="text-xs text-muted">{t.voices.existingNote}</p>
          {list.isPending && <div aria-busy="true" className="h-11" />}
          {list.data?.listed && (
            <>
              {list.data.slots_limit > 0 && (
                <p className="text-xs text-muted" data-testid="voice-slots">
                  {t.voices.slots(list.data.slots_used, list.data.slots_limit)}
                </p>
              )}
              {list.data.voices.length === 0 ? (
                <p className="text-sm text-muted">{t.voices.noAccountVoices}</p>
              ) : (
                <ul className="flex flex-wrap gap-2" data-testid={`voice-account-list-${id}`}>
                  {list.data.voices.map((v) => (
                    <li key={v.voice_id}>
                      <button
                        type="button"
                        aria-pressed={current === v.voice_id}
                        disabled={busy}
                        onClick={() => use.mutate({ voiceID: v.voice_id, label: "" })}
                        className={`min-h-11 rounded-full border px-4 py-2 text-sm transition-colors disabled:opacity-40 ${
                          current === v.voice_id
                            ? "border-sage bg-sage text-on-sage"
                            : "border-line hover:bg-sunk"
                        }`}
                      >
                        <bdi>
                          {use.isPending && use.variables?.voiceID === v.voice_id
                            ? t.voices.using
                            : v.name || v.voice_id}
                        </bdi>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {(list.isError || (list.data && !list.data.listed)) && (
            <form
              className="space-y-2"
              data-testid={`voice-account-form-${id}`}
              onSubmit={(e) => {
                e.preventDefault();
                use.mutate({ voiceID: voiceID.trim(), label: label.trim() });
              }}
            >
              {list.data?.reason && <p className="text-xs text-muted">{list.data.reason}</p>}
              <label className="block text-xs text-muted">
                {t.voices.voiceID}
                <input
                  value={voiceID}
                  onChange={(e) => setVoiceID(e.target.value)}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  dir="ltr"
                  className="mt-1 block w-full rounded-control border border-line bg-surface px-3 py-2 font-mono text-base"
                />
              </label>
              <label className="block text-xs text-muted">
                {t.voices.voiceLabel}
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  dir="auto"
                  className="mt-1 block w-full rounded-control border border-line bg-surface px-3 py-2 text-base"
                />
              </label>
              <button
                type="submit"
                disabled={busy || voiceID.trim() === ""}
                className="min-h-11 w-full rounded-control border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
              >
                {use.isPending ? t.voices.using : t.voices.useVoice}
              </button>
            </form>
          )}
          <MutationError
            testId={`voice-existing-error-${id}`}
            error={use.error}
            fallback={t.voices.failed}
          />
        </div>
      )}
    </div>
  );
}

/**
 * A voice from the vendor's public library (requirement 0090's amendment),
 * found by a search the server builds from the card's description. Each
 * match's preview is the vendor's own clip, free to play; choosing adds the
 * voice to the account — no voice slot, but one of the month's voice adds —
 * and costs nothing (ADR-0172).
 * Pricier voices are never offered (the server filters them).
 */
function LibraryVoices({
  bookID,
  role,
  description,
  disabled,
  playing,
  onPlay,
}: {
  bookID: string;
  role: CastRole;
  description: string;
  disabled: boolean;
  playing: string | null;
  onPlay: (v: Playable) => void;
}) {
  const t = useT();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  // The description searched: set on opening, and again on asking, so typing does not search.
  const [searched, setSearched] = useState(description);
  const id = roleID(role);
  const found = useQuery({
    queryKey: ["library-voices", bookID, id, searched],
    queryFn: () => findLibraryVoices(bookID, role, searched),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const use = useMutation({
    mutationFn: (v: LibraryVoice) => castLibraryVoice(bookID, role, v),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: castKey(bookID) });
      void client.invalidateQueries({ queryKey: accountVoicesKey(bookID) });
      void client.invalidateQueries({ queryKey: ["library-voices", bookID] });
      if (isNarrator(role))
        void client.invalidateQueries({ queryKey: bookKey(bookID, { draft: true }) });
      setOpen(false);
    },
  });
  const busy = disabled || use.isPending;
  const current = role.voice?.vendor_voice_id;

  return (
    <div className="mt-2">
      <button
        type="button"
        data-testid={`voice-library-${id}`}
        aria-expanded={open}
        onClick={() => {
          if (!open) setSearched(description);
          setOpen((o) => !o);
        }}
        className="min-h-11 text-sm text-sage-deep underline-offset-4 hover:underline"
      >
        {open ? t.voices.hideLibrary : t.voices.findLibrary}
      </button>
      {open && (
        <div className="mt-2 space-y-3" data-testid={`voice-library-panel-${id}`}>
          <p className="text-xs text-muted">{t.voices.libraryNote}</p>
          <p className="text-xs text-muted">{t.voices.libraryWithdraw}</p>
          {found.data && found.data.add_edits_limit > 0 && (
            <p className="text-xs text-muted" data-testid={`voice-library-adds-${id}`}>
              {t.voices.libraryAdds(found.data.add_edits_used, found.data.add_edits_limit)}
            </p>
          )}
          {description !== searched && (
            <button
              type="button"
              data-testid={`voice-library-again-${id}`}
              onClick={() => setSearched(description)}
              className="min-h-11 rounded-control border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk"
            >
              {t.voices.librarySearchAgain}
            </button>
          )}
          {found.isPending && <div aria-busy="true" className="h-11" />}
          {found.data?.relaxed && found.data.voices.length > 0 && (
            <p className="text-xs text-muted">{t.voices.libraryRelaxed}</p>
          )}
          {found.data && found.data.voices.length === 0 && (
            <p className="text-sm text-muted">{t.voices.libraryNone}</p>
          )}
          {found.data && found.data.voices.length > 0 && (
            <ul className="space-y-3" data-testid={`voice-library-list-${id}`}>
              {found.data.voices.map((v) => (
                <li
                  key={v.voice_id}
                  className="flex items-start gap-3"
                  data-testid={`voice-library-match-${v.voice_id}`}
                >
                  <PlayButton
                    voice={{ id: `library-${v.voice_id}`, sample_url: v.preview_url }}
                    playing={playing}
                    onPlay={onPlay}
                    label={t.voices.libraryPreview(v.name)}
                  />
                  <div className="min-w-0 flex-1" dir="auto">
                    <p className="text-sm font-medium">
                      {v.name}
                      {v.category === "high_quality" && (
                        <span
                          data-testid={`voice-library-hq-${v.voice_id}`}
                          title={t.voices.libraryHQTitle}
                          className="ms-2 rounded-full border border-sage px-1.5 py-0.5 align-middle text-[0.65rem] font-medium"
                        >
                          {t.voices.libraryHQ}
                        </span>
                      )}
                    </p>
                    {v.description && (
                      <p className="line-clamp-3 text-xs text-muted">{v.description}</p>
                    )}
                    <p className="mt-1 text-xs text-muted">
                      {v.in_account
                        ? t.voices.libraryInAccount
                        : v.notice_days > 0
                          ? t.voices.libraryNotice(v.notice_days)
                          : null}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`voice-library-choose-${v.voice_id}`}
                    aria-pressed={current === v.voice_id}
                    disabled={busy}
                    onClick={() => use.mutate(v)}
                    className="min-h-11 shrink-0 rounded-control border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
                  >
                    {use.isPending && use.variables?.voice_id === v.voice_id
                      ? t.voices.choosing
                      : t.voices.choose}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <MutationError
            testId={`voice-library-error-${id}`}
            error={found.error ?? use.error}
            fallback={t.voices.failed}
          />
        </div>
      )}
    </div>
  );
}

function PlayButton({
  voice,
  playing,
  onPlay,
  label,
}: {
  voice: Playable;
  playing: string | null;
  onPlay: (v: Playable) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={`voice-play-${voice.id}`}
      onClick={() => onPlay(voice)}
      className="grid size-11 shrink-0 place-items-center rounded-full border border-line text-sm transition-colors hover:bg-sunk"
    >
      <span aria-hidden="true">{playing === voice.id ? "◼" : "▶"}</span>
    </button>
  );
}
